<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { reactive, onMounted, computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import CategoryLayout from '@/Layouts/CategoryLayout.vue';
import ProductCarousel from '@/Components/Carousels/ProductCarousel.vue';
import ProductLinkList from '@/Components/ProductLinkList.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import BrandStrip from '@/Components/Category/BrandStrip.vue';
import { marked } from 'marked';
import { sanitizeHtml } from '@/Utils/sanitize';
import NyhedsBlok from '@/Components/NyhedsBlok.vue';

const page = usePage();

const nyheder = computed(() => page.props.nyheder || []);

const user = computed(
    () => page.props.auth.user
);

const breadcrumbs = computed(
    () => page.props.breadcrumbs
);

const pageTitle = computed(() => {
    return page.props.pageTitle;
});

const subCategories = computed(() => page.props.subCategories);

const popularCatProducts = computed(() => page.props.popularCatProducts);
const productLinks = computed(() => page.props.productLinks || []);

// Prisoversigten. Se kommentaren ved sektionen nedenfor.
const prisoversigt = computed(() => page.props.prisoversigt || null);
const prisoversigtOverskrift = computed(() => page.props.prisoversigtOverskrift || '');
// Kategoriens prisbevægelse den seneste måned, fra vores egne målinger.
// Null når tallene ikke siger noget; se KategoriPrisudvikling.
const prisudvikling = computed(() => page.props.prisudvikling || null);
const faq = computed(() => page.props.faq || []);

const currentCategory = computed(() => page.props.category);

const seoData = computed(() => page.props.seo ?? {});

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});

const state = reactive({
    loading: false,
});

// Track expanded sub-categories
const expandedCategories = ref({});

// Toggle expanded state for a category
const toggleCategory = (categoryId) => {
    expandedCategories.value[categoryId] = !expandedCategories.value[categoryId];
};

// Check if a category is expanded
const isExpanded = (categoryId) => {
    return !!expandedCategories.value[categoryId];
};

// Computed properties for SEO text with markdown parsing
const topSeoText = computed(() =>
    currentCategory.value?.top_seo_intro
        ? sanitizeHtml(marked.parse(currentCategory.value.top_seo_intro))
        : ''
);

const bottomSeoText = computed(() =>
    currentCategory.value?.bottom_seo_intro
        ? sanitizeHtml(marked.parse(currentCategory.value.bottom_seo_intro))
        : ''
);

onMounted(async () => {
    // Initialize all categories as collapsed by default
    subCategories.value.forEach(category => {
        if (category.subSubCategories && category.subSubCategories.length > 0) {
            expandedCategories.value[category.slug] = false;
        }
    });
});
</script>

<template>
    <CategoryLayout>
        <div class="container-fluid text-center g-0">
            <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            <!--
                Overskriften rammer prissøgningerne.

                h1 var kategorinavnet alene: "Bundkort", "Grafikkort", "Tv".
                De prisord folk faktisk søger på, er "tv priser", "billig tv",
                "tv tilbud" og "billig server", og ordet "priser" stod ikke i
                en eneste h1 på sitet.
            -->
            <h1 class="h3 mb-3 mt-3 text-start px-3">{{ currentCategory.name }} priser</h1>
            <BrandStrip v-if="page.props.categoryBrands && page.props.categoryBrands.length > 0" :brands="page.props.categoryBrands" class="mx-3 mb-3" />
            <div class="row g-0">
                <div class="col-md-3 col-lg-2">
                    <div class="card rounded-0 category-sidebar">
                        <div class="card-header bg-pricecompare-primary text-white">
                            <h5 class="mb-0">Kategorier</h5>
                        </div>
                        <div class="card-body p-0">
                            <ul class="category-nav">
                                <li v-for="category in subCategories" :key="category.slug" 
                                    :class="{'has-children': category.subSubCategories && category.subSubCategories.length > 0}">
                                    <div class="category-item">
                                        <a :href="category.url" class="category-link">{{ category.name }}</a>
                                        <button v-if="category.subSubCategories && category.subSubCategories.length > 0" 
                                                class="toggle-btn" 
                                                @click.prevent="toggleCategory(category.slug)">
                                            <i class="bi" :class="isExpanded(category.slug) ? 'bi-dash' : 'bi-plus'"></i>
                                        </button>
                                    </div>
                                    
                                    <!-- Sub-sub-categories -->
                                    <transition name="slide">
                                        <ul v-if="category.subSubCategories && category.subSubCategories.length > 0" 
                                            v-show="isExpanded(category.slug)" 
                                            class="subcategory-nav">
                                            <li v-for="subSubCat in category.subSubCategories" :key="subSubCat.id">
                                                <a :href="subSubCat.url || '#'" class="subcategory-link">
                                                    <i class="bi bi-caret-right-fill me-2"></i>{{ subSubCat.name }}
                                                </a>
                                            </li>
                                        </ul>
                                    </transition>
                                </li>
                            </ul>
                        </div> 
                    </div>  
                </div>
                <div class="col-md-9 col-lg-10 main-content-block p-2">
                    <div class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <div v-if="topSeoText && topSeoText.trim() !== ''" class="text-muted small" v-html="topSeoText"></div>
                            <div v-else-if="seoData.description" class="text-muted small">
                                {{ seoData.description }}
                            </div>
                        </div>
                    </div>

                    <!--
                      PRISOVERSIGTEN STAAR NU FOER KARRUSELLERNE.
                      Foer laa den under tre raekker produktbilleder, saa det
                      foerste skaermbillede var billeder og produktnavne og
                      intet andet — selvom siden har knap 6.000 tegn indhold.
                      Det er ogsaa det eneste paa siden ingen anden dansk
                      aktoer kan svare paa: hvad varerne koster lige nu, og om
                      priserne er paa vej op eller ned.
                    -->
                                        <!--
                        Hvad koster varerne her?

                        279 prissøgeord ("billig server", "tv tilbud",
                        "overvågningskamera pris") giver 1.944 eksponeringer på
                        gennemsnitsposition 35,4 og TO klik. For hver af dem
                        findes der en kategoriside, men den hedder "Tv" og har
                        179 tegn brødtekst. Der fandtes ingen side der svarede
                        på spørgsmålet.

                        Tallene er vores egne målinger og opdateres af sig selv.
                    -->
                    <section v-if="prisoversigt" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">{{ prisoversigtOverskrift }}</h2>
                            <p class="mb-0" style="color: var(--pc-text-secondary);">{{ prisoversigt.tekst }}</p>
                            <!--
                              Bevægelsen, ikke bare niveauet. "Er priserne på vej
                              ned" er det spørgsmål en køber faktisk stiller, og
                              svaret står i HTML så både Google og en sprogmodel
                              kan læse det.
                            -->
                            <p v-if="prisudvikling" class="mb-0 mt-3" style="color: var(--pc-text-secondary);">{{ prisudvikling.tekst }}</p>
                        </div>
                    </section>

                    <div v-for="(category, name) in popularCatProducts">
                        <div class="card">
                            <div class="card-text">
                                <ProductCarousel :componentIdentifier="'carousel_' + name" :products="category" :headline="'Populære produkter i ' + name" :productsPrPage="4" />
                            </div>
                        </div>
                        <br/>
                    </div>



                    <!--
                      Spoergsmaal og svar, samme mekanik som produktsiderne hvor
                      den virker. Svarene staar i HTML og ikke kun i JSON-LD: et
                      svar der kun findes i skema-opmaerkning kan Google afvise
                      som skjult indhold, og et menneske faar intet ud af det.
                      Tom liste naar der ikke er nok at sige; se KategoriFaq.
                    -->
                    <section v-if="faq.length" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <h2 class="h5 mb-3">Ofte stillede spørgsmål</h2>
                            <div v-for="(q, i) in faq" :key="i" :class="i > 0 ? 'mt-3' : ''">
                                <h3 class="h6 mb-1">{{ q.spoergsmaal }}</h3>
                                <p class="mb-0" style="color: var(--pc-text-secondary);">{{ q.svar }}</p>
                            </div>
                        </div>
                    </section>

                    <ProductLinkList :products="productLinks" title="Populære produkter" />

                    <div v-if="bottomSeoText && bottomSeoText.trim() !== ''" class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body py-4 px-4 text-start">
                            <div class="seo-long-text text-muted" v-html="bottomSeoText"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="container">
            <NyhedsBlok :nyheder="nyheder" overskrift="Nyheder i denne kategori" />
        </div>
    </CategoryLayout>
</template>

<style scoped>
.main-content-block {
    /*
     * Samme værn som i SubSubCategory: en Bootstrap-col kan ikke blive
     * smallere end sit indholds min-content, og en produktliste med en
     * ubrudt streng i et navn brækker så kolonnen ned under filtrene.
     */
    min-width: 0;
}

/* Updated styling for category sidebar */
.category-sidebar {
    border: none;
    box-shadow: var(--pc-shadow);
    margin-bottom: 1.5rem;
}

.bg-pricecompare-primary {
    background-color: var(--pc-accent);
}

.category-nav {
    list-style: none;
    padding: 0;
    margin: 0;
}

.category-nav > li {
    border-bottom: 1px solid var(--pc-border);
}

.category-nav > li:last-child {
    border-bottom: none;
}

.category-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.category-link {
    display: block;
    padding: 0.75rem 1rem;
    color: var(--pc-accent);
    text-decoration: none;
    font-weight: 500;
    flex-grow: 1;
    text-align: left;
    transition: all 0.2s;
}

.category-link:hover {
    background-color: var(--pc-bg-hover);
    color: var(--pc-accent-hover);
}

.toggle-btn {
    background: none;
    border: none;
    color: var(--pc-text-secondary);
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.25rem 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.toggle-btn:hover {
    color: var(--pc-accent);
}

.has-children > .category-item {
    border-bottom: none;
}

.subcategory-nav {
    list-style: none;
    padding: 0;
    margin: 0;
    background-color: var(--pc-bg-tertiary);
}

.subcategory-link {
    display: block;
    padding: 0.5rem 1rem 0.5rem 2rem;
    color: var(--pc-text-primary);
    text-decoration: none;
    font-size: 0.9rem;
    text-align: left;
    transition: all 0.2s;
}

.subcategory-link:hover {
    color: var(--pc-accent);
    background-color: var(--pc-bg-hover);
}

.subcategory-link .bi {
    font-size: 0.7rem;
    color: var(--pc-accent);
}

/* Animation for expanding/collapsing subcategories */
.slide-enter-active, .slide-leave-active {
    transition: max-height 0.3s ease, opacity 0.3s ease;
    max-height: 500px;
    overflow: hidden;
    opacity: 1;
}

.slide-enter-from, .slide-leave-to {
    max-height: 0;
    opacity: 0;
}
/* For smaller screens make the sidebar full width and collapsible */
@media (max-width: 767.98px) {
    .category-sidebar {
        margin-bottom: 1rem;
    }

    .category-nav {
        max-height: none;
    }
}
</style>